import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import { Toaster } from "@/components/ui/sonner";

const PhysicsUniverse = lazy(() => import("@/pages/PhysicsUniverse"));
const TopicView = lazy(() => import("@/pages/TopicView"));
const SimulationView = lazy(() => import("@/pages/SimulationView"));
const VirtualLab = lazy(() => import("@/pages/VirtualLab"));
const GraphStudio = lazy(() => import("@/pages/GraphStudio"));
const AITutor = lazy(() => import("@/pages/AITutor"));
const Practice = lazy(() => import("@/pages/Practice"));
const Notes = lazy(() => import("@/pages/Notes"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const TeacherStudio = lazy(() => import("@/pages/TeacherStudio"));
const AdventureHub = lazy(() => import("@/feature/adventure/pages/AdventureHub"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/register" element={<Auth mode="register" />} />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route
                path="universe"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PhysicsUniverse />
                  </Suspense>
                }
              />
              <Route
                path="topic/:topicId"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TopicView />
                  </Suspense>
                }
              />
              <Route
                path="sim/:simId"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SimulationView />
                  </Suspense>
                }
              />
              <Route
                path="adventure"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AdventureHub />
                  </Suspense>
                }
              />
              <Route
                path="lab"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <VirtualLab />
                  </Suspense>
                }
              />
              <Route
                path="graphs"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <GraphStudio />
                  </Suspense>
                }
              />
              <Route
                path="tutor"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AITutor />
                  </Suspense>
                }
              />
              <Route
                path="practice"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Practice />
                  </Suspense>
                }
              />
              <Route
                path="notes"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Notes />
                  </Suspense>
                }
              />
              <Route
                path="analytics"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Analytics />
                  </Suspense>
                }
              />
              <Route
                path="teacher"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TeacherStudio />
                  </Suspense>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
